import { useState } from "react";
import axios from "axios";
import PageBanner from "./PageBanner";
import SocialMediaSidebar from "./SocialMediaSidebar";
import { useNavigate } from "react-router-dom";
import ProgressBar from "./ProgressBar";
import { EcomAPI,CMSAPI } from "../config";
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// Helper function to get cookies
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
};

const OrderProcessingModal = ({ currentStep, statusMessage }) => {
  const steps = [
    'Verifying your cart',
    'Validating discount code',
    'Processing payment',
    'Confirming order',
    'Completing purchase'
  ];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'white',
        padding: '2rem',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '500px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
      }}>
        <Typography variant="h6" gutterBottom>
          Processing Your Order
        </Typography>
        
        <Box sx={{ width: '100%', mb: 3 }}>
          <LinearProgress 
            variant="determinate" 
            value={(currentStep / steps.length) * 100} 
          />
        </Box>

        <div>
          {steps.map((step, index) => (
            <div key={index} style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '1rem',
              opacity: index < currentStep ? 0.8 : index === currentStep ? 1 : 0.6,
              fontWeight: index === currentStep ? '500' : 'normal'
            }}>
              <div style={{
                width: '24px',
                height: '24px',
                borderRadius: '50%',
                background: index < currentStep ? '#00D084' : '#f0f0f0',
                color: index < currentStep ? 'white' : 'inherit',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginRight: '1rem',
                flexShrink: 0
              }}>
                {index < currentStep ? '✓' : index === currentStep ? (
                  <CircularProgress size={20} />
                ) : (
                  index + 1
                )}
              </div>
              <div>
                <Typography variant="body1">{step}</Typography>
                {index === currentStep && statusMessage && (
                  <Typography variant="caption" color="textSecondary">
                    {statusMessage}
                  </Typography>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Checkout = () => {
  const [discountCode, setDiscountCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const isValidDiscountCode = async (code) => {
    try {
      const response = await axios.get(`${CMSAPI}/api/Website/get-discount`);
      const discountCodes = response.data;
      return discountCodes.some((item) => item.discountCode === code);
    } catch (err) {
      console.error("Error validating discount code:", err);
      return false;
    }
  };

  const placeOrder = async () => {
    setIsLoading(true);
    setIsProcessing(true);
    setCurrentStep(0);
    setError("");

    const userId = getCookie("userId");
    const cartId = getCookie("cartId");
    const userEmail = getCookie("userName");

    if (!userId || !cartId || !userEmail) {
      setIsLoading(false);
      setIsProcessing(false);
      setError("User, cart, or email information missing. Please log in again.");
      return;
    }

    if (!discountCode) {
      setIsLoading(false);
      setIsProcessing(false);
      setError("Please enter a discount code to proceed.");
      return;
    }

    try {
      // Step 1: Cart verification
      setCurrentStep(0);
      setStatusMessage("Checking your cart items...");
      
      const [cartCheckRes, isValid] = await Promise.all([
        axios.get(`${CMSAPI}/api/Website/get-cart-by-user-id?userId=${userId}`),
        discountCode ? isValidDiscountCode(discountCode) : Promise.resolve(true)
      ]);

      // Step 2: Cart validation
      setCurrentStep(1);
      setStatusMessage("Validating your cart contents...");
      
      if (!Array.isArray(cartCheckRes.data) || cartCheckRes.data.length === 0) {
        setError("Your cart is empty.");
        return;
      }

      // Step 3: Discount validation
      setCurrentStep(2);
      setStatusMessage("Applying your discount code...");
      
      if (discountCode && !isValid) {
        setError("Invalid coupon code. Please try again.");
        return;
      }

      const payload = {
        cartId: Number(cartId),
        userId: Number(userId),
        totalAmount: 0,
        discountCode: discountCode || "",
      };

      // Step 4: Order processing
      setCurrentStep(3);
      setStatusMessage("Finalizing your order details...");
      
      const response = await axios.post(
        `${CMSAPI}/api/Website/add-to-Order`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            accept: "*/*",
          },
        }
      );

      // Step 5: Completion
      setCurrentStep(4);
      setStatusMessage("Order confirmed! Redirecting...");
      
      if (response.status === 200) {
        setTimeout(() => navigate("/order-success"), 1000);
      }
    } catch (error) {
      console.error("Error during order placement:", error);
      setError(
        error.response?.data?.message ||
          "An error occurred while placing the order."
      );
    } finally {
      setIsLoading(false);
      if (!error) {
        setTimeout(() => setIsProcessing(false), 1500);
      } else {
        setIsProcessing(false);
      }
    }
  };

  return (
    <>
      <div>
        <SocialMediaSidebar />
      </div>
      <PageBanner
        title="Checkout"
        breadcrumb={[
          { label: "Home", href: "/" },
          { label: "Resources" },
          { label: "SwachhAJV Shop" },
        ]}
      />
      <div>
        <ProgressBar currentStep={3} />
      </div>

      <div className="max-w-6xl mx-auto p-4">
        <div className="card-body py-3 px-4 border-b">
          <div className="flex">
            <input
              type="text"
              className="flex-grow p-2 border rounded-l focus:outline-none focus:ring-2 focus:ring-[#00D084]"
              placeholder="Enter Coupon Code"
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value)}
            />
            <button
              className="bg-white text-[#00D084] border border-[#00D084] px-4 py-2 rounded-r hover:bg-blue-50 transition-colors"
              onClick={placeOrder}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-[#00D084]"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                "Place Order"
              )}
            </button>
          </div>
          {error && <div className="mt-2 text-red-500 text-sm">{error}</div>}
        </div>
      </div>

      {isProcessing && (
        <OrderProcessingModal 
          currentStep={currentStep} 
          statusMessage={statusMessage} 
        />
      )}
    </>
  );
};

export default Checkout;